import { ReactElement, useMemo } from 'react';
import { Typography, Stack } from '@mui/material';
import {
  faLayerGroup as SubscriptionsIcon,
  faHourglassStart as ActiveDateIcon,
  faCalendarTimes as ExpiredDateIcon,
  faRotate as CycleIcon,
  faMoneyBill as MoneyIcon,
  faPercent as DiscountIcon,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';

import { formatterDate, formatCurrencyValue } from '~utils/index';

import {
  SubscriptionPlanCardRoot,
  SubscriptionPlanCardContent,
} from '../../PlanCard/styles';
import SubscriptionPlanCardRowInfo from '../../PlanCard/RowInfo';
import { useSubscriptionPaymentModalContext } from '../SubscriptionPaymentModalContext';
import { getPaymentTypeMultiplier, getPaymentTypeAclaration } from '../utils';

import { SubscriptionPaymentModalDetailsRoot } from './styles';

const currentDate = moment().startOf('day');

const SubscriptionPaymentModalDetails = (): ReactElement => {
  const { paymentType, itemData, isArg, isSinglePayment, discount } =
    useSubscriptionPaymentModalContext();

  const expiredDate = useMemo(() => {
    const monthsToAdd = getPaymentTypeMultiplier(paymentType);

    return currentDate.clone().add(monthsToAdd, 'months').startOf('day');
  }, [paymentType]);

  const activatedText = useMemo(
    () => (isSinglePayment ? 'Fecha de alta:' : 'Inicio del ciclo:'),
    [isSinglePayment],
  );

  const expiredText = useMemo(
    () => (isSinglePayment ? 'Vencimiento:' : 'Cierre de ciclo:'),
    [isSinglePayment],
  );

  const currentPrice = useMemo(() => itemData.price.USD, [itemData.price]);

  const priceWithDiscount = useMemo(() => {
    if (!discount) return undefined;

    return currentPrice * (1 - discount / 100);
  }, [currentPrice, discount]);

  return (
    <SubscriptionPaymentModalDetailsRoot>
      <Stack spacing={2}>
        <Typography fontWeight="bold">Detalle de tu selección</Typography>

        <SubscriptionPlanCardRoot>
          <SubscriptionPlanCardContent sx={{ gridTemplateColumns: '1fr' }}>
            <Stack spacing={0.5}>
              <SubscriptionPlanCardRowInfo
                value={itemData.title}
                icon={SubscriptionsIcon}
                textColor="primary"
                iconColor="primary"
              />

              <SubscriptionPlanCardRowInfo
                description={activatedText}
                value={formatterDate(currentDate, {
                  format: 'DD/MM/YYYY',
                })}
                icon={ActiveDateIcon}
                textColor="textSecondary"
              />

              {(!isArg || isSinglePayment || paymentType === 'annual') && (
                <SubscriptionPlanCardRowInfo
                  description={expiredText}
                  value={formatterDate(expiredDate, {
                    format: 'DD/MM/YYYY',
                  })}
                  icon={ExpiredDateIcon}
                  textColor="textSecondary"
                />
              )}

              {!isSinglePayment && (
                <SubscriptionPlanCardRowInfo
                  description="Ciclo de facturación:"
                  value={getPaymentTypeAclaration(paymentType)}
                  icon={CycleIcon}
                  textColor="textSecondary"
                />
              )}

              <SubscriptionPlanCardRowInfo
                description="A pagar:"
                value={formatCurrencyValue(currentPrice)}
                icon={MoneyIcon}
                textColor={priceWithDiscount ? 'textSecondary' : 'textPrimary'}
                textDecoration={priceWithDiscount ? 'line-through' : undefined}
              />

              {priceWithDiscount && (
                <SubscriptionPlanCardRowInfo
                  description={`${discount}% OFF:`}
                  value={formatCurrencyValue(priceWithDiscount)}
                  icon={DiscountIcon}
                  textColor="textPrimary"
                />
              )}
            </Stack>
          </SubscriptionPlanCardContent>
        </SubscriptionPlanCardRoot>
      </Stack>

      <Stack>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" fontWeight={500}>
            TOTAL USD
          </Typography>

          <Typography variant="h6" component="span" fontWeight="bold">
            {formatCurrencyValue(priceWithDiscount ?? currentPrice, {
              style: 'decimal',
              minimumFractionDigits: 2,
            })}
          </Typography>
        </Stack>
      </Stack>
    </SubscriptionPaymentModalDetailsRoot>
  );
};

export default SubscriptionPaymentModalDetails;
